import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DatePicker } from '@ge/components/datepicker';
import { Icon, Icons } from '@ge/components/icon';
import { Text } from '@ge/components/typography';
import { DateTimeFormats } from '@ge/models/constants';
import { typography } from '@ge/tokens';

import { useManualAdjustmentContext } from '../../context/manual-adjustment-context';

import { DialogLabel, DialogValue } from './event-dialog-style';

const DateValue = styled.div`
  font-size: 11px;
  line-height: 20px;
  text-align: center;
  padding: 2px 14px;
  border-radius: 2px;
  max-width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  background: ${(props) => props.theme.button.primary.backgroundColor};
  border: ${(props) => props.theme.button.primary.border};
  color: ${(props) => props.theme.button.primary.textColor};
  text-shadow: ${(props) => props.theme.button.primary.textShadow};

  .react-datepicker.react-datepicker__day {
    width: 25px !important;
  }
`;

const InputWrapper = styled.div`
  .react-datepicker .react-datepicker__day {
    width: 25px !important;
  }
  .react-datepicker .react-datepicker__children-container {
    width: 10.5rem;
  }
`;

const CalendarIcon = styled(Icon).attrs(({ theme }) => ({
  size: 12,
  color: theme.analyze.header.calendarIconColor,
  icon: Icons.CALENDAR,
}))`
  margin-left: 6px;
  vertical-align: initial;
`;

const ErrorText = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
  level: 2,
}))`
  margin: 2px -3px -10px -2px;
  color: ${(props) => props.theme.dangerColor};
  display: block;
  .custom-body-2 {
    font-size: 11px;
    line-height: 10px;
  }
`;

const MalEventDateDuration = ({ label, name }) => {
  const { t } = useTranslation(['analyze.manual-adjustment'], {
    useSuspense: false,
  });
  const { calendarDate } = useManualAdjustmentContext();
  const { control } = useFormContext();
  const [eventDate, setEventDate] = useState({
    startDate: calendarDate,
    endDate: calendarDate,
  });
  const [dateError, setDateError] = useState(false);
  const datepickerRef = useRef();

  const eventDateError = useMemo(() => {
    return (
      <ErrorText>
        <span className="custom-body-2">
          {t(
            'event_details_dialog.invalid_startDate_error',
            'Start Date should be less than today.',
          )}
        </span>
      </ErrorText>
    );
  }, [t]);

  const getCustomDateString = () => {
    const start = dayjs(eventDate.startDate).format(DateTimeFormats.DEFAULT_DATE);
    const end = eventDate.endDate && dayjs(eventDate.endDate).format(DateTimeFormats.DEFAULT_DATE);

    return `${start} ${end && start !== end ? `- ${end}` : ''}`;
  };

  return (
    <div>
      <DialogLabel>{label}</DialogLabel>
      <DialogValue>
        <InputWrapper>
          <Controller
            control={control}
            name={name}
            defaultValue={eventDate}
            render={({ onChange }) => (
              <DatePicker
                selectsRange
                startDate={eventDate.startDate}
                endDate={eventDate.endDate}
                onChange={(date) => {
                  onChange({
                    startDate: date[0],
                    endDate: date[1],
                  });
                  setEventDate({
                    startDate: date[0],
                    endDate: date[1],
                  });
                  if (
                    dayjs(date[0]).isAfter(dayjs(dayjs().startOf('date').subtract(1, 'day')), 'day')
                  ) {
                    setDateError(true);
                  } else setDateError(false);
                }}
                onCalendarClose={() => {
                  if (!dayjs(eventDate.endDate).isValid()) {
                    setEventDate((prev) => ({
                      ...prev,
                      endDate: prev.startDate,
                    }));
                    onChange({
                      startDate: eventDate.startDate,
                      endDate: eventDate.startDate,
                    });
                  }
                }}
                ref={datepickerRef}
                popperPlacement="bottom-start"
                customInput={
                  <DateValue
                    onClick={() => {
                      datepickerRef.current.setOpen(true);
                    }}
                  >
                    {getCustomDateString()} <CalendarIcon />
                  </DateValue>
                }
                shouldCloseOnSelect={false}
                onApplyClick={() => {
                  datepickerRef.current.setOpen(false);
                }}
              />
            )}
          />
        </InputWrapper>
      </DialogValue>
      {dateError && <DialogValue>{eventDateError}</DialogValue>}
    </div>
  );
};

MalEventDateDuration.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  timezone: PropTypes.string,
};

export default MalEventDateDuration;
